import { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Form,
  Select,
  DatePicker,
  Checkbox,
  Divider,
  Row,
  Col,
  Collapse,
} from "antd";
import dayjs from "dayjs";
import WizardStepLayout from "./WizardStepLayout";
import {
  saCellphoneFormRule,
  saTelephoneFormRule,
} from "../../utils/systemValidation";

import type { SelectProps, CheckboxProps } from "antd";

type BeneficiarySource = "main" | "covered" | "other";

interface StepProps {
  gotoStep: Function;
  current: number;
  handleAge: Function;
  form: any;
  /** Covered lives available for beneficiary selection. */
  coveredLives?: any[];
}

const titleOpt: SelectProps["options"] = [
  { name: "BenTitle", label: "Mr", value: "Mr" },
  { name: "BenTitle", label: "Ms", value: "Ms" },
  { name: "BenTitle", label: "Mx", value: "Mx" },
  { name: "BenTitle", label: "Mrs", value: "Mrs" },
  { name: "BenTitle", label: "Miss", value: "Miss" },
  { name: "BenTitle", label: "Madam", value: "Madam" },
];

const gender: SelectProps["options"] = [
  { name: "BenGender", label: "Male", value: "Male" },
  { name: "BenGender", label: "Female", value: "Female" },
];

const benRelationship: SelectProps["options"] = [
  { name: "BenRelationship", label: "Father", value: "Father" },
  { name: "BenRelationship", label: "Mother", value: "Mother" },
  { name: "BenRelationship", label: "Brother", value: "Brother" },
  { name: "BenRelationship", label: "Sister", value: "Sister" },
  { name: "BenRelationship", label: "Son", value: "Son" },
  { name: "BenRelationship", label: "Daughter", value: "Daughter" },
  { name: "BenRelationship", label: "Spouse", value: "Spouse" },
  { name: "BenRelationship", label: "Life Partner", value: "Life Partner" },
  { name: "BenRelationship", label: "Stepchild", value: "Stepchild" },
  { name: "BenRelationship", label: "Grandchild", value: "Grandchild" },
  { name: "BenRelationship", label: "Stepparent", value: "Stepparent" },
  { name: "BenRelationship", label: "Grandparent", value: "Grandparent" },
  { name: "BenRelationship", label: "Aunt", value: "Aunt" },
  { name: "BenRelationship", label: "Uncle", value: "Uncle" },
  { name: "BenRelationship", label: "Niece", value: "Niece" },
  { name: "BenRelationship", label: "Nephew", value: "Nephew" },
  { name: "BenRelationship", label: "Brother-in-law", value: "Brother-in-law" },
  { name: "BenRelationship", label: "Sister-in-law", value: "Sister-in-law" },
  { name: "BenRelationship", label: "Mother-in-law", value: "Mother-in-law" },
  { name: "BenRelationship", label: "Father-in-law", value: "Father-in-law" },
  { name: "BenRelationship", label: "Son-in-law", value: "Son-in-law" },
  { name: "BenRelationship", label: "Daughter-in-law", value: "Daughter-in-law" },
  { name: "BenRelationship", label: "Friend", value: "Friend" },
];

const sourceOptions: SelectProps["options"] = [
  { label: "Main member", value: "main" },
  { label: "Covered life", value: "covered" },
  { label: "Other person", value: "other" },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 8 },
};

const clearBeneficiaryFields = {
  BenTitle: undefined,
  BenInitials: undefined,
  BenName: undefined,
  BenSurname: undefined,
  BenCellphone: undefined,
  BenTelNumber: undefined,
  BenEmail: undefined,
  BenIdNumber: undefined,
  BenDateOfBirth: undefined,
  BenGender: undefined,
  BenRelationship: undefined,
  BenEmployerName: undefined,
  BenAddressLine1: undefined,
  BenAddressLine2: undefined,
  BenSuburb: undefined,
  BenProvince: undefined,
  BenPostalCode: undefined,
  MainAddress: false,
};

const toDayjs = (value: any) => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) return value;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : undefined;
};

const genderFromId = (idNumber: string): string | undefined => {
  if (!idNumber || idNumber.length < 10) return undefined;
  const checkGender = idNumber.substring(6, 10);
  return Number(checkGender) < 5000 ? "Female" : "Male";
};

const Beneficiary: React.FC<StepProps> = ({
  gotoStep,
  current,
  handleAge,
  form,
  coveredLives = [],
}) => {
  const [benSource, setBenSource] = useState<BeneficiarySource>("other");
  const [coveredIndex, setCoveredIndex] = useState<number | null>(null);
  const [mainAddress, setMainAddress] = useState(false);

  const coveredOptions = useMemo(
    () =>
      (Array.isArray(coveredLives) ? coveredLives : []).map((life, index) => ({
        value: index,
        label: [
          life?.CoveredName,
          life?.CoveredSurname,
          life?.CoveredType ? `(${life.CoveredType})` : null,
        ]
          .filter(Boolean)
          .join(" "),
      })),
    [coveredLives]
  );

  const extractDateOfBirth = (idNumber: string) => {
    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);
    const currentYear = new Date().getFullYear();
    const birthYear = year + (year < currentYear % 100 ? 2000 : 1900);
    return new Date(birthYear, month - 1, day);
  };

  const handleDateOfBirth = (idNumber: string) => {
    if (!idNumber || idNumber.length < 6) return;
    const dateOfBirth = extractDateOfBirth(idNumber);
    form.setFieldsValue({
      BenDateOfBirth: dayjs(dateOfBirth).add(2, "hour"),
    });
    handleAge(dateOfBirth);
  };

  const applyMainMember = () => {
    const values = form.getFieldsValue(true);
    const idNumber = values.IdNumber ?? values.IDNumber ?? "";
    const dob =
      toDayjs(values.DateOfBirth) ??
      (idNumber ? dayjs(extractDateOfBirth(String(idNumber))).add(2, "hour") : undefined);
    const resolvedGender =
      values.Gender || genderFromId(String(idNumber)) || undefined;

    form.setFieldsValue({
      ...clearBeneficiaryFields,
      BenTitle: values.Title,
      BenInitials: values.Initials,
      BenName: values.Name,
      BenSurname: values.Surname,
      BenCellphone: values.Cellphone,
      BenTelNumber: values.TelNumber,
      BenEmail: values.Email,
      BenIdNumber: idNumber,
      BenDateOfBirth: dob,
      BenGender: resolvedGender,
      BenRelationship: values.BenRelationship || undefined,
      BenEmployerName: values.EmployerName,
      MainAddress: true,
      BenAddressLine1: values.AddressLine1,
      BenAddressLine2: values.AddressLine2,
      BenSuburb: values.Suburb,
      BenProvince: values.Province,
      BenPostalCode: values.PostalCode,
    });
    setMainAddress(true);
    if (dob) handleAge(dob.toDate());
  };

  const applyCoveredLife = (index: number) => {
    const life = coveredLives[index];
    if (!life) return;
    const idNumber = life.CoveredIdNumber ?? "";
    const dob =
      toDayjs(life.CoveredDateOfBirth) ??
      (idNumber ? dayjs(extractDateOfBirth(String(idNumber))).add(2, "hour") : undefined);
    const relationshipOptions = new Set(
      (benRelationship ?? []).map((opt) => String(opt?.value))
    );
    const relationship =
      life.CoveredType && relationshipOptions.has(String(life.CoveredType))
        ? life.CoveredType
        : undefined;

    form.setFieldsValue({
      ...clearBeneficiaryFields,
      BenName: life.CoveredName,
      BenSurname: life.CoveredSurname,
      BenIdNumber: idNumber,
      BenDateOfBirth: dob,
      BenGender: genderFromId(String(idNumber)),
      BenRelationship: relationship,
      MainAddress: false,
    });
    setMainAddress(false);
    if (dob) handleAge(dob.toDate());
    if (idNumber) handleDateOfBirth(String(idNumber));
  };

  const handleSourceChange = (value: BeneficiarySource) => {
    setBenSource(value);
    setCoveredIndex(null);
    if (value === "main") {
      applyMainMember();
      return;
    }
    if (value === "other") {
      form.setFieldsValue(clearBeneficiaryFields);
      setMainAddress(false);
      return;
    }
    // covered — wait for covered life selection
    form.setFieldsValue(clearBeneficiaryFields);
    setMainAddress(false);
  };

  const handleCoveredSelect = (index: number) => {
    setCoveredIndex(index);
    applyCoveredLife(index);
  };

  const onChange: CheckboxProps["onChange"] = () => {
    setMainAddress((prev: any) => {
      const newValue = !prev;
      form.setFieldsValue({
        MainAddress: newValue,
      });
      return newValue;
    });
  };

  const onChangeId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id: string = e.target.value;
    const checkGender = id.substring(6, 10);

    if (Number(checkGender) < 5000) {
      form.setFieldsValue({ BenGender: "Female" });
      handleDateOfBirth(id);
    } else {
      form.setFieldsValue({ BenGender: "Male" });
      handleDateOfBirth(id);
    }
  };

  useEffect(() => {
    const { MainAddress } = form.getFieldsValue();
    if (MainAddress) {
      setMainAddress(MainAddress);
    }
  });

  const colProps = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    style: { marginBottom: 8 },
  };

  const fieldsReadOnly = benSource === "main" || benSource === "covered";

  const content = (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Form.Item>
      }
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Beneficiary source"
            required
            {...colProps}
          >
            <Select
              size="large"
              options={sourceOptions}
              value={benSource}
              onChange={handleSourceChange}
              style={{ width: "100%" }}
              placeholder="Who is the beneficiary?"
            />
          </Form.Item>
        </Col>
        {benSource === "covered" && (
          <Col span={12}>
            <Form.Item
              label="Covered life"
              required
              {...colProps}
            >
              <Select
                size="large"
                options={coveredOptions}
                value={coveredIndex ?? undefined}
                onChange={handleCoveredSelect}
                style={{ width: "100%" }}
                placeholder={
                  coveredOptions.length
                    ? "Select covered life"
                    : "No covered lives added yet"
                }
                disabled={!coveredOptions.length}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="BenName" label="Name" rules={[{ required: true }]} {...colProps}>
            <Input size="large" placeholder="Name" disabled={fieldsReadOnly} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="BenSurname" label="Surname" rules={[{ required: true }]} {...colProps}>
            <Input size="large" placeholder="Surname" disabled={fieldsReadOnly} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="BenCellphone"
            label="Cellphone"
            rules={[saCellphoneFormRule]}
            {...colProps}
          >
            <Input size="large" placeholder="Cellphone" disabled={fieldsReadOnly && benSource === "main"} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="BenIdNumber" label="ID Number" rules={[{ required: true }]} {...colProps}>
            <Input
              size="large"
              placeholder="ID Number"
              onChange={(e) => onChangeId(e)}
              max={13}
              disabled={fieldsReadOnly}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="BenDateOfBirth" label="Date Of Birth" rules={[{ required: true }]} {...colProps}>
            <DatePicker
              placeholder="Date Of Birth"
              size="large"
              style={{ width: "100%" }}
              disabled={fieldsReadOnly}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="BenGender" label="Gender" rules={[{ required: true }]} {...colProps}>
            <Select
              options={gender}
              size="large"
              allowClear
              style={{ width: "100%" }}
              placeholder="Gender"
              disabled={fieldsReadOnly}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="BenRelationship" label="Relationship" {...colProps}>
            <Select
              options={benRelationship}
              size="large"
              allowClear
              style={{ width: "100%" }}
              placeholder="Relationship"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="BenEmployerName" label="Employer Name" {...colProps}>
            <Input size="large" placeholder="Employer Name" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="MainAddress" label="Use Main Member's Address" {...colProps}>
            <Checkbox checked={mainAddress} onChange={onChange} />
          </Form.Item>
        </Col>
      </Row>

      {!mainAddress && (
        <>
          <Divider orientation="left" plain style={{ marginTop: 4, marginBottom: 4 }}>
            Address
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="BenAddressLine1" label="Address Line 1" rules={[{ required: true }]} {...colProps}>
                <Input size="large" placeholder="Address Line 1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="BenAddressLine2" label="Address Line 2" rules={[{ required: true }]} {...colProps}>
                <Input size="large" placeholder="Address Line 2" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="BenSuburb" label="Suburb" rules={[{ required: true }]} {...colProps}>
                <Input size="large" placeholder="Suburb" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="BenProvince" label="Province" rules={[{ required: true }]} {...colProps}>
                <Input size="large" placeholder="Province" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="BenPostalCode"
                label="Postal Code"
                rules={[{ required: true, message: "Please input beneficiary postal code!" }]}
                {...colProps}
              >
                <Input size="large" placeholder="Postal Code" />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Collapse ghost style={{ marginBottom: 8 }}>
        <Collapse.Panel header="Optional" key="optional">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="BenTitle" label="Title" {...colProps}>
                <Select options={titleOpt} size="large" allowClear style={{ width: "100%" }} placeholder="Title" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="BenInitials" label="Initials" {...colProps}>
                <Input size="large" placeholder="Initials" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="BenTelNumber"
                label="Telephone Number"
                rules={[saTelephoneFormRule]}
                {...colProps}
              >
                <Input size="large" placeholder="Telephone Number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="BenEmail" label="Email" rules={[{ type: "email" }]} {...colProps}>
                <Input size="large" placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
    </WizardStepLayout>
  );

  return content;
};

export default Beneficiary;
